import { Controller } from "@hotwired/stimulus"

// Dock metrics + auto-hide.
//
// 1. MEASURE. --app-dock-height and --app-dock-clearance were hardcoded to
//    72px/132px, which already drift from the rendered bar and go stale
//    whenever the dock changes. Measure the real bar and publish both.
//
// 2. AUTO-HIDE. Padding only protects the END of the document, so a CTA at a
//    natural mid-page reading position is still covered by a fixed bar. Hide
//    on scroll down, reveal on scroll up. Always revealed at rest, at the top,
//    and while any submenu/overlay is open.
export default class extends Controller {
  connect() {
    this.lastY = window.scrollY
    this.idleTimer = null
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this._onScroll = this._handleScroll.bind(this)
    this._onResize = this._measure.bind(this)

    window.addEventListener("scroll", this._onScroll, { passive: true })
    window.addEventListener("resize", this._onResize, { passive: true })

    // Re-measure when the dock's own contents change (submenu opens, badge
    // appears, secondary stack expands) so clearance never goes stale.
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this._measure())
      this.observer.observe(this.element)
    }

    this._measure()
  }

  disconnect() {
    window.removeEventListener("scroll", this._onScroll)
    window.removeEventListener("resize", this._onResize)
    if (this.observer) this.observer.disconnect()
    if (this.idleTimer) clearTimeout(this.idleTimer)
    this.element.classList.remove("dock-hidden")
  }

  _measure() {
    // Measure the visible bar, not the container, which may also hold an
    // expanded secondary stack.
    const bar = this.element.querySelector(".app-dock")
    const source = bar || this.element
    // The cockpit nav is a vertical rail above 900px: it reserves width, not
    // bottom clearance, so publish zero there.
    const isRail = !bar && getComputedStyle(source).flexDirection === "column"
    const height = isRail ? 0 : Math.round(source.getBoundingClientRect().height)

    const root = document.documentElement
    if (height > 0) {
      root.style.setProperty("--app-dock-height", `${height}px`)
      // Clearance = the bar plus breathing room, so content never sits flush
      // against it. The previous hardcoded pair was 72/132, a 60px gap.
      root.style.setProperty("--app-dock-clearance", `${height + 60}px`)
    } else {
      // Dock is not displayed at this viewport/input combination, so nothing
      // needs reserving. Leaving a stale clearance would pad every page for a
      // bar that is not there.
      root.style.setProperty("--app-dock-height", "0px")
      root.style.setProperty("--app-dock-clearance", "0px")
    }
  }

  _handleScroll() {
    const y = window.scrollY
    const delta = y - this.lastY

    // Ignore sub-pixel jitter and rubber-band overscroll.
    if (Math.abs(delta) < 6) return
    this.lastY = y

    if (this._mustStayVisible()) {
      this._reveal()
      return
    }

    if (delta > 0 && y > 80) {
      this._hide()
    } else {
      this._reveal()
    }

    // Revealed at rest: if scrolling stops, bring it back.
    if (this.idleTimer) clearTimeout(this.idleTimer)
    this.idleTimer = setTimeout(() => this._reveal(), 1200)
  }

  // Never hide the dock while the user is interacting with something anchored
  // to it, or at the very top of the page.
  _mustStayVisible() {
    if (window.scrollY <= 80) return true
    if (this.element.classList.contains("axle-overlay-open")) return true
    if (this.element.querySelector(".dock-submenu.open, .dock-secondary.open")) return true
    if (document.body.classList.contains("avatar-editor-open")) return true

    const expanded = this.element.querySelector('[aria-expanded="true"]')
    return !!expanded
  }

  _hide() {
    this.element.classList.add("dock-hidden")
  }

  _reveal() {
    this.element.classList.remove("dock-hidden")
  }
}
